import React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Check from '@mui/icons-material/Check';
import PricingPlan from './PricingPlan';
import { pricingTableData } from './models/pricingTableData';

type Props = {
    data: pricingTableData;
    onSelectPlan: (pricingPlanId: string) => void;
};

const data: pricingTableData = {
    features: [
        {
            id: 1,
            name: '@mui/base',
            tooltip: 'The unstyled components and react hooks.'
        },
        {
            id: 2,
            name: '@mui/material',
            tooltip: 'Core components following Material Design.'
        },
        {
            id: 3,
            name: '@mui/system',
            tooltip: 'CSS utilities for rapidly laying out custom designs.'
        },
        {
            id: 4,
            name: 'Data Grid Pro Updates'
        },
        {
            id: 5,
            name: 'Date Range Picker'
        }
    ],
    plans: [
        {
            id: 'Community',
            name: 'Community',
            description: 'Get started with the industry-standard UI library for building React user interfaces (open-source, MIT licensed).',
            price: 0,
            featuresValues: [
                {
                    id: 1,
                    value: true
                },
                {
                    id: 2,
                    value: true
                },
                {
                    id: 3,
                    value: true
                },
                {
                    id: 4,
                    value: false
                },
                {
                    id: 5,
                    value: false
                }
            ]
        },
        {
            id: 'Pro',
            name: 'Pro',
            description: 'Best for professional developers building enterprise or data-rich applications.',
            price: 186,
            per: 'Developer',
            featuresValues: [
                {
                    id: 1,
                    value: true
                },
                {
                    id: 2,
                    value: true
                },
                {
                    id: 3,
                    value: true
                },
                {
                    id: 4,
                    value: '1 year'
                },
                {
                    id: 5,
                    value: true
                }
            ]
        },
        {
            id: 'Premium',
            name: 'Premium',
            description: 'Unlock all the most advanced features and professional support.',
            price: 599,
            featuresValues: [
                {
                    id: 1,
                    value: true
                },
                {
                    id: 2,
                    value: true
                },
                {
                    id: 3,
                    value: true
                },
                {
                    id: 4,
                    value: true
                },
                {
                    id: 5,
                    value: true
                }
            ]
        }
    ]
};

const PricingTable = ({ onSelectPlan }: Props) => {
    return (
        <TableContainer component={StyledPaper}>
            <Table sx={{ minWidth: 100 }} aria-label="simple table" style={{ height: '100%' }}>
                <TableHead style={{ height: '100%' }}>
                    <StyledTableRow style={{ height: '100%' }}>
                        <StyledTableCell></StyledTableCell>
                        {data.plans.map((plan) => (
                            <StyledTableCell align="center" style={{ height: '100%' }}>
                                <PricingPlan
                                    onSelect={onSelectPlan}
                                    planId={plan.id}
                                    planName={plan.name}
                                    price={plan.price}
                                    description={plan.description}
                                    per={plan.per}
                                />
                            </StyledTableCell>
                        ))}
                    </StyledTableRow>
                </TableHead>
                <TableBody>
                    {data.features.map((feature) => (
                        <StyledTableRow key={feature.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <StyledTableCell component="th" scope="row">
                                {feature.name}{' '}
                                {feature.tooltip && (
                                    <Tooltip title={feature.tooltip} placement="right">
                                        <StyledInfoIcon />
                                    </Tooltip>
                                )}
                            </StyledTableCell>
                            {data.plans.map((plan) => {
                                const featureValue = plan.featuresValues.find((fv) => fv.id === feature.id)?.value || '-';

                                if (featureValue === true) {
                                    return (
                                        <StyledTableCell align="center">
                                            <CheckIcon />
                                        </StyledTableCell>
                                    );
                                }

                                return <StyledTableCell align="center">{featureValue}</StyledTableCell>;
                            })}
                        </StyledTableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    fontFamily: 'IBM Plex Sans, sans-serif'
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.white,
        borderLeft: '1px solid rgba(224, 224, 224, 1) !important',
        paddingTop: 5,
        width: 200
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: 20,
        borderLeft: '1px solid rgba(224, 224, 224, 1) !important',
        '&:first-child': {
            borderLeft: 0
        }
    },
    [`&.${tableCellClasses.body}:first-child, &.${tableCellClasses.head}:first-child`]: {
        borderLeft: '0 !important'
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const StyledInfoIcon = styled(InfoIcon)(({ theme }) => ({
    fontSize: 16,
    verticalAlign: 'middle',
    marginLeft: 5,
    color: '#3E5060',
    '&:hover, &.Mui-focusVisible': {
        color: theme.palette.primary.main
    }
}));

const CheckIcon = styled(Check)(({ theme }) => ({
    fontSize: 20,
    verticalAlign: 'middle',
    color: theme.palette.primary.main
}));

export default PricingTable;
